import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent } from '@azure/msal-angular';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { msalConfig, loginRequest, apiConfig } from './auth-config';
import { DevAuthInterceptor } from './dev-auth-interceptor';
import { IntlModule } from "@progress/kendo-angular-intl";

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ButtonsModule } from "@progress/kendo-angular-buttons";
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { DropDownsModule, CheckAllDirective } from '@progress/kendo-angular-dropdowns';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { EditorModule } from "@progress/kendo-angular-editor";
import { InputsModule, SliderModule, RangeSliderModule, FormFieldModule } from '@progress/kendo-angular-inputs';
import { IconsModule, SVGIconModule } from "@progress/kendo-angular-icons";
import { LayoutModule } from '@progress/kendo-angular-layout';
import { LabelModule, FloatingLabelModule } from '@progress/kendo-angular-label';
import { PopupModule } from '@progress/kendo-angular-popup';
import { TreeViewModule } from '@progress/kendo-angular-treeview';
import { UploadsModule } from '@progress/kendo-angular-upload';
import { GridModule } from '@progress/kendo-angular-grid';
import { MenusModule } from "@progress/kendo-angular-menu";
import { ChartsModule } from '@progress/kendo-angular-charts';
import { PDFExportModule } from "@progress/kendo-angular-pdf-export";
import { NavigationModule } from "@progress/kendo-angular-navigation";
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { TreeListModule } from '@progress/kendo-angular-treelist';
import { ProgressBarModule } from '@progress/kendo-angular-progressbar';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { PopoverModule } from '@progress/kendo-angular-tooltip'
import { ConversationalUIModule } from "@progress/kendo-angular-conversational-ui";
import { MentionModule } from 'angular-mentions';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {TranslateModule} from "@ngx-translate/core";
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ReactiveFormsModule,FormsModule } from '@angular/forms';
import { CreateComponent } from '../app/features/rta/create/create.component';
import { EditComponent } from '../app/features/rta/edit/edit.component';
import { DatailsComponent } from '../app/features/rta/datails/datails.component';
import { EjdfComponent } from '../app/features/ejdf/ejdf.component';
import { IrtaComponent } from '../app/features/irta/irta.component';
import { HomeComponent } from '../app/features/home/home.component';
import { DashboardComponent } from '../app/features/dashboard/dashboard.component';
import { AnalyticsComponent } from './features/dashboard/analytics/analytics.component';
import { BitwitComponent } from './features/bitwit/bitwit.component';
import { HeaderComponent } from '../app/layout/header/header.component';
import { FooterComponent } from '../app/layout/footer/footer.component';
import { NavigationComponent } from './layout/navigation/navigation.component';
import { BasicDataComponent } from './shared/components/basic-data/basic-data.component';
import { AuthLoadingComponent } from './shared/components/auth-loading/auth-loading.component';
import { UnauthorizedComponent } from './shared/components/unauthorized/unauthorized.component';
import { NgxsModule } from '@ngxs/store';
import { AccountState } from './shared/store/state/account.state';
import { environment } from '../environments/environment';
import { OptionState } from './shared/store/state/options.state';
import { EstimatedCogsComponent } from './shared/components/estimated-cogs/estimated-cogs.component';
import { RequestState } from './shared/store/state/request.state';
import { SecondaryDataComponent } from './shared/components/secondary-data/secondary-data.component';
import { FixedcutterDataComponent } from './shared/components/fixedcutter-data/fixedcutter-data.component';
import { PowderbinderDataComponent } from './shared/components/powderbinder-data/powderbinder-data.component';
import { BitfeatureDataComponent } from './shared/components/bitfeature-data/bitfeature-data.component';
import { GaugpadprotectionDataComponent } from './shared/components/gaugpadprotection-data/gaugpadprotection-data.component';
import { ArmDataComponent } from './shared/components/arm-data/arm-data.component';
import { RollerconeDataComponent } from './shared/components/rollercone-data/rollercone-data.component';
import { CuttermappingDataComponent } from './shared/components/cuttermapping-data/cuttermapping-data.component';
import { HistoryDataComponent } from './shared/components/history-data/history-data.component';
import { CommunicationDataComponent } from './shared/components/communication-data/communication-data.component';
import { ReviewrequestDataComponent } from './shared/components/reviewrequest-data/reviewrequest-data.component';
import { PopulatesapDataComponent } from './shared/components/populatesap-data/populatesap-data.component';
import { SapdocumentsDataComponent } from './shared/components/sapdocuments-data/sapdocuments-data.component';
import { RouterDataComponent } from './shared/components/router-data/router-data.component';
import { FilevaultDataComponent } from './shared/components/filevault-data/filevault-data.component';
import { BrazeinportsDataComponent } from './shared/components/populatesap-subcomponents/brazeinports-data/brazeinports-data.component';
import { CastinportsDataComponent } from './shared/components/populatesap-subcomponents/castinports-data/castinports-data.component';
import { GaugeprotectionDataComponent } from './shared/components/populatesap-subcomponents/gaugeprotection-data/gaugeprotection-data.component';
import { GaugestepsDataComponent } from './shared/components/populatesap-subcomponents/gaugesteps-data/gaugesteps-data.component';
import { HardfacingDataComponent } from './shared/components/populatesap-subcomponents/hardfacing-data/hardfacing-data.component';
import { NozzlelinersDataComponent } from './shared/components/populatesap-subcomponents/nozzleliners-data/nozzleliners-data.component';
import { PowdersDataComponent } from './shared/components/populatesap-subcomponents/powders-data/powders-data.component';
import { MarketingsheetDataComponent } from './shared/components/populatesap-subcomponents/marketingsheet-data/marketingsheet-data.component';
import { NozzledisplacementsDataComponent } from './shared/components/populatesap-subcomponents/nozzledisplacements-data/nozzledisplacements-data.component';
import { ProcesstaskDataComponent } from './shared/components/processtask-data/processtask-data.component';
import { PartelementDataComponent } from './shared/components/populatesap-subcomponents/partelement-data/partelement-data.component';
import { FixedcuttersapDataComponent } from './shared/components/populatesap-subcomponents/fixedcuttersap-data/fixedcuttersap-data.component';
import { SapPartNumbersComponent } from './shared/components/populatesap-subcomponents/sap-part-numbers/sap-part-numbers.component';
import { CreateMaterialMasterBitsComponent } from './shared/components/populatesap-subcomponents/sap-part-numbers/create-material-master-bits/create-material-master-bits.component';
import { BomDataComponent } from './shared/components/bom-data/bom-data.component';
import { ConnectionStatusComponent } from './shared/components/connection-status/connection-status.component';
import { EmailEditorModule } from './shared/components/email-designer/email-editor.module';
import { AdminComponent } from './features/admin/admin.component';
import { EmailTemplatesComponent } from './features/admin/email-templates/email-templates.component';
import { EmailDesignerComponent } from './features/admin/email-designer/email-designer.component';
import { DistributionListsComponent } from './features/admin/distribution-lists/distribution-lists.component';
import { NotificationLogComponent } from './features/admin/notification-log/notification-log.component';
import { RouterTemplatesComponent } from './features/admin/router-templates/router-templates.component';
import { FeatureRouterMappingComponent } from './features/admin/feature-router-mapping/feature-router-mapping.component';
import { SendBulletinComponent } from './features/admin/send-bulletin/send-bulletin.component';
import { ProcessTemplatesComponent } from './features/admin/process-templates/process-templates.component';
import { ResourceAssignmentsComponent } from './features/admin/resource-assignments/resource-assignments.component';
import { DeploymentBulletinComponent } from './shared/components/deployment-bulletin/deployment-bulletin.component';
import { SettingsComponent } from './features/settings/settings.component';
import { ApprovalActionComponent } from './features/rta/approval-action/approval-action.component';
import { ClickOutsideDirective } from './shared/directives/click-outside.directive';
import { MarketingPhotosComponent } from './shared/components/marketing-sheet-photos/marketing-sheet-photos.component';
import {
  AffectedItemsComponent,
  BomCompareComponent,
  BomEditorComponent,
  BomTreeComponent,
  ChangeCreateComponent,
  ChangeDashboardComponent,
  ChangeDetailComponent,
  ChangeListComponent,
  ChangeWorkflowComponent,
  FileExplorerComponent,
  FileListComponent,
  FileUploadComponent,
  FileViewerComponent,
  LifecycleActionsComponent,
  LifecycleBadgeComponent,
  LifecycleHistoryComponent,
  MyTasksComponent,
  NotificationCenterComponent,
  NotificationPreferencesComponent,
  SavedSearchesComponent,
  SearchBarComponent,
  SearchFiltersComponent,
  SearchResultsComponent,
  TaskDetailComponent,
  UserRoleAdminComponent,
  VersionHistoryComponent,
  WhereUsedComponent,
  WorkflowHistoryComponent,
  WorkflowTemplateAdminComponent,
  WorkflowVisualizerComponent,
  DocumentLinksComponent,
  DrawingNumberRequestComponent,
  DrawingNumberListComponent,
  FILEVAULT_API_URL 
} from '@filevault/components';
//import { SecondaryDataComponent } from './views/partials/secondary-data/secondary-data.component';
//import { BitDataComponent } from './views/partials/bit-data/bit-data.component';
//import { CuttermapDataComponent } from './views/partials/cuttermap-data/cuttermap-data.component';
//import { GaugeDataComponent } from './views/partials/gauge-data/gauge-data.component';
//import { ReviewComponent } from './views/partials/review/review.component';
//import { GaugeStepsComponent } from './views/resources/components/gauge-steps/gauge-steps.component';
//import { HardfacingComponent } from './views/resources/components/hardfacing/hardfacing.component';
//import { PowdersComponent } from './views/resources/components/powders/powders.component';
//import { NozzleLinersComponent } from './views/resources/components/nozzle-liners/nozzle-liners.component';
//import { BrazeinPortsComponent } from './views/resources/components/brazein-ports/brazein-ports.component';
//import { NozzleDisplacementsComponent } from './views/resources/components/nozzle-displacements/nozzle-displacements.component';
//import { CastinPortsComponent } from './views/resources/components/castin-ports/castin-ports.component';
//import { RouterComponent } from './views/resources/components/router/router.component';
//import { SapDocumentsComponent } from './views/resources/components/sap-documents/sap-documents.component';
//import { MarketingSheetComponent } from './views/resources/components/marketing-sheet/marketing-sheet.component';
//import { FeaturesComponent } from './views/resources/components/features/features.component';
//import { GlobalNotificationService } from './core/services/globalnotification.service';
//import { FilevaultComponent } from './views/partials/filevault/filevault.component';
//import { PopulateSapComponent } from './views/partials/populate-sap/populate-sap.component';
//import { SecondaryGaugeCuttersComponent } from './views/resources/components/secondary-gauge-cutters/secondary-gauge-cutters.component';
//import { EditTableService } from './core/services/edit-table.service';
//import { GaugeProtectionComponent } from './views/resources/components/gauge-protection/gauge-protection.component';
//import { RtaService } from './core/data/rta.service';

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    CreateComponent,
    EjdfComponent,
    IrtaComponent,
    HomeComponent,
    DatailsComponent,
    DashboardComponent,
    AnalyticsComponent,
    BitwitComponent,
    EditComponent,
    NavigationComponent,
    BasicDataComponent,
    //BasicDataComponent,
    //FixedcutterDataComponent,
    EstimatedCogsComponent,
    SecondaryDataComponent,
    FixedcutterDataComponent,
    PowderbinderDataComponent,
    BitfeatureDataComponent,
    GaugpadprotectionDataComponent,
    ArmDataComponent,
    RollerconeDataComponent,
    CuttermappingDataComponent,
    HistoryDataComponent,
    CommunicationDataComponent,
    ReviewrequestDataComponent,
    PopulatesapDataComponent,
    SapdocumentsDataComponent,
    RouterDataComponent,
    FilevaultDataComponent,
    BrazeinportsDataComponent,
    CastinportsDataComponent,
    GaugeprotectionDataComponent,
    GaugestepsDataComponent,
    HardfacingDataComponent,
    NozzlelinersDataComponent,
    PowdersDataComponent,
    MarketingsheetDataComponent,
    NozzledisplacementsDataComponent,
    ProcesstaskDataComponent,
    PartelementDataComponent,
    FixedcuttersapDataComponent,
    SapPartNumbersComponent,
    CreateMaterialMasterBitsComponent,
    BomDataComponent,
    AdminComponent,
    EmailTemplatesComponent,
    EmailDesignerComponent,
    DistributionListsComponent,
    NotificationLogComponent,
    RouterTemplatesComponent,
    FeatureRouterMappingComponent,
    SendBulletinComponent,
    DeploymentBulletinComponent,
    SettingsComponent,
    ApprovalActionComponent,
    MarketingPhotosComponent,
    ProcessTemplatesComponent,
    ResourceAssignmentsComponent
    //ClickOutsideDirective
  ],
  imports: [
    CommonModule,
    AppRoutingModule,
    BrowserModule,
    FormsModule,
    BrowserAnimationsModule,
    ButtonsModule,
    DropDownsModule,
    DateInputsModule,
    EditorModule,
    InputsModule,
    SliderModule,
    RangeSliderModule,
    FormFieldModule,
    LayoutModule,
    LabelModule,
    FloatingLabelModule,
    PopupModule,
    TreeViewModule,
    UploadsModule,
    HttpClientModule,
    IndicatorsModule,
    GridModule,
    TreeListModule,
    MenusModule,
    NavigationModule,
    IconsModule,
    SVGIconModule,
    PDFExportModule,
    ProgressBarModule,
    DialogsModule,
    ConversationalUIModule,
    MentionModule,
    FontAwesomeModule,
    ReactiveFormsModule,
    PopoverModule,
    IntlModule,
    EmailEditorModule,
    NotificationModule,
    ChartsModule,
    MsalModule,
    AuthLoadingComponent,
    UnauthorizedComponent,
    NgxsModule.forRoot([AccountState, OptionState, RequestState], {
        developmentMode: !environment.production
    }),
    TranslateModule.forRoot(),
     ConnectionStatusComponent,
     AffectedItemsComponent,
     BomCompareComponent,
     BomEditorComponent,
     BomTreeComponent,
     ChangeCreateComponent,
     ChangeDashboardComponent,
     ChangeDetailComponent,
     ChangeListComponent,
     ChangeWorkflowComponent,
     FileExplorerComponent,
     FileListComponent,
     FileUploadComponent,
     FileViewerComponent,
     LifecycleActionsComponent,
     LifecycleBadgeComponent,
     LifecycleHistoryComponent,
     MyTasksComponent,
     NotificationCenterComponent,
     NotificationPreferencesComponent,
     SavedSearchesComponent,
     SearchBarComponent,
     SearchFiltersComponent,
     SearchResultsComponent,
     TaskDetailComponent,
     UserRoleAdminComponent,
     VersionHistoryComponent,
     WhereUsedComponent,
     WorkflowHistoryComponent,
     WorkflowTemplateAdminComponent,
     WorkflowVisualizerComponent,
     DrawingNumberRequestComponent,
     DrawingNumberListComponent,
     DocumentLinksComponent
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideTranslateHttpLoader({ prefix: './i18n/', suffix: '.json' }),
    {
      provide: LOCALE_ID,
      useValue: "en-US",
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: environment.devBypassAuth ? DevAuthInterceptor : MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MsalService],
      multi: true
    },
    { provide: FILEVAULT_API_URL, useValue: environment.fileVaultApiUrl }
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }

/**
 * MSAL Instance Factory
 */
let msalInstanceCache: IPublicClientApplication | null = null;

export function MSALInstanceFactory(): IPublicClientApplication {
  if (!msalInstanceCache) {
    msalInstanceCache = new PublicClientApplication(msalConfig);
  }
  return msalInstanceCache;
}

/**
 * Initialize MSAL before app starts (skipped in dev bypass mode)
 */
export function initializeMsal(msalInstance: MsalService): () => Promise<void> {
  return () => {
    if (environment.devBypassAuth) {
      return Promise.resolve();
    }
    return msalInstance.instance.initialize();
  };
}

/**
 * MSAL Guard Configuration Factory
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest
  };
}

/**
 * MSAL Interceptor Configuration Factory
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  
  // Add protected API endpoint with scopes from config
  protectedResourceMap.set(apiConfig.baseUrl, apiConfig.scopes);
  
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}
